// Unit chip colors — super-admin section on the Locality edit page.
//
// Colors are data per locality (API: /admin/unit-color-scheme/:localityId).
// The simple model shown here: the locality's real UNIT TYPES (the letters
// before the first number: E, TL, BN, RESCUE…) each get a color CATEGORY from
// a dropdown. Under the hood that is a `unitType` matcher on the category's
// rule; prefix/regex matchers (used by the FDNY preset) live under "Advanced".
// The server resolves the colors the apps render, so the preview is real: it
// round-trips the draft through POST …/preview against the real units.
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, Col, Form, Modal, Row, Spinner, Table } from "react-bootstrap";
import { api, common } from "includes";

type Matcher = { type: "prefix" | "regex" | "unitType"; value: string };
type Rule = {
  key: string; label: string; sort: number; matchers: Matcher[];
  bg: string; fg?: string; bgDark?: string; fgDark?: string; legend?: string; legendDark?: string;
};
type Override = { unit: string; bg: string; fg?: string; bgDark?: string; fgDark?: string; category?: string };
type Resolved = { unit: string; bg: string; fg: string; bgDark: string; fgDark: string; category: string };
type Legend = { key: string; label: string; color: string; colorDark: string };
type UnitType = { type: string; count: number; examples: string[] };

type Props = { localityId: string; localityName?: string };

const HEX = /^#([0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
const isHex = (v?: string) => !!v && HEX.test(v);
const rgb6 = (v?: string) => (isHex(v) ? "#" + v!.slice(1, 7) : "#000000");
const NONE = "__none__";

// WCAG contrast, mirrors the server's check so the warning shows while editing.
const lum = (hex: string) => {
  const h = hex.slice(1, 7);
  const c = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16) / 255)
    .map((v) => (v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)));
  return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
};
const contrast = (a: string, b: string) => {
  const la = lum(a), lb = lum(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
};
const autoFg = (bg: string) => (contrast(bg, "#000000") >= contrast(bg, "#FFFFFF") ? "#000000" : "#FFFFFF");

// A handful of ready-made chip colors so a super admin never has to type hex.
const SWATCHES: { name: string; bg: string }[] = [
  { name: "Black", bg: "#1C1C1E" }, { name: "Red", bg: "#E5342A" }, { name: "Yellow", bg: "#F7C948" },
  { name: "Green", bg: "#17B26A" }, { name: "Orange", bg: "#E0842F" }, { name: "Blue", bg: "#2F6FE4" },
  { name: "Purple", bg: "#7A4DD8" }, { name: "Teal", bg: "#0FA3A8" }, { name: "White", bg: "#FFFFFF" }, { name: "Gray", bg: "#9AA0AC" },
];

const matchersToText = (ms: Matcher[], type: "prefix" | "regex") =>
  ms.filter((m) => m.type === type).map((m) => m.value).join(type === "prefix" ? ", " : "\n");
const textToMatchers = (text: string, type: "prefix" | "regex"): Matcher[] =>
  text.split(type === "prefix" ? "," : "\n").map((s) => s.trim()).filter(Boolean).map((value) => ({ type, value }));

export default function UnitColorsPanel({ localityId, localityName }: Props) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>("");
  const [configured, setConfigured] = useState(false);
  const [presetTag, setPresetTag] = useState<string | null>(null);
  const [presets, setPresets] = useState<{ key: string; name: string }[]>([]);
  const [gray, setGray] = useState<any>({ bg: "#E7E8ED", fg: "#6C6F78" });
  const [units, setUnits] = useState<string[]>([]);
  const [unitTypes, setUnitTypes] = useState<UnitType[]>([]);
  const [rules, setRules] = useState<Rule[]>([]);
  const [overrides, setOverrides] = useState<Override[]>([]);
  const [preview, setPreview] = useState<Resolved[]>([]);
  const [legend, setLegend] = useState<Legend[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [dirty, setDirty] = useState(false);
  const [unitSearch, setUnitSearch] = useState("");
  const [dark, setDark] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [confirm, setConfirm] = useState<"preset" | "reset" | null>(null);
  const previewTimer = useRef<any>(null);

  const applyPayload = (d: any) => {
    setConfigured(!!d.configured);
    setPresetTag(d.preset ?? null);
    setPresets(d.presets ?? []);
    setGray(d.gray ?? gray);
    setUnits(d.units ?? []);
    setUnitTypes(d.unitTypes ?? []);
    setRules((d.rules ?? []).map((r: any) => ({ ...r, matchers: [...(r.matchers ?? [])] })));
    setOverrides((d.overrides ?? []).map((o: any) => ({ ...o })));
    setPreview(d.preview ?? []);
    setLegend(d.legend ?? []);
    setWarnings(d.warnings ?? []);
    setDirty(false);
    setError("");
  };

  const load = useCallback(() => {
    setLoading(true);
    api.call({ url: `admin/unit-color-scheme/${localityId}`, method: "GET" }, (res: any) => {
      setLoading(false);
      if (res.status === 200) applyPayload(res.data);
      else setError(res.data?.error || "Could not load unit colors");
    });
    // applyPayload only touches state setters; re-creating it per render would refetch on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localityId]);

  useEffect(() => { load(); }, [load]);

  // Live preview: debounce the draft through the server resolver.
  const refreshPreview = useCallback((draftRules: Rule[], draftOverrides: Override[]) => {
    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(() => {
      api.call({
        url: `admin/unit-color-scheme/${localityId}/preview`, method: "POST",
        body: { rules: draftRules, overrides: draftOverrides, units },
      }, (res: any) => {
        if (res.status === 200) { setPreview(res.data.preview ?? []); setLegend(res.data.legend ?? []); setWarnings(res.data.warnings ?? []); setError(""); }
        else setError(res.data?.error || "Preview failed");
      });
    }, 300);
  }, [localityId, units]);

  const updateRules = (next: Rule[]) => { setRules(next); setDirty(true); refreshPreview(next, overrides); };
  const updateOverrides = (next: Override[]) => { setOverrides(next); setDirty(true); refreshPreview(rules, next); };

  const save = () => {
    setSaving(true);
    api.call({
      url: `admin/unit-color-scheme/${localityId}`, method: "PUT",
      body: { preset: presetTag, rules, overrides },
    }, (res: any) => {
      setSaving(false);
      if (res.status === 200) { applyPayload(res.data); common.notify("S", "Unit colors saved"); }
      else { setError(res.data?.error || "Save failed"); common.notify("E", res.data?.error || "Save failed"); }
    });
  };

  const applyPreset = (name: string) => {
    setSaving(true);
    api.call({ url: `admin/unit-color-scheme/${localityId}/preset/${name}`, method: "POST", body: {} }, (res: any) => {
      setSaving(false); setConfirm(null);
      if (res.status === 200) { applyPayload(res.data); common.notify("S", `${name.toUpperCase()} preset applied`); }
      else common.notify("E", res.data?.error || "Preset failed");
    });
  };

  const resetToGray = () => {
    setSaving(true);
    api.call({ url: `admin/unit-color-scheme/${localityId}`, method: "DELETE" }, (res: any) => {
      setSaving(false); setConfirm(null);
      if (res.status === 200) { applyPayload(res.data); common.notify("S", "Reset: all units gray"); }
      else common.notify("E", res.data?.error || "Reset failed");
    });
  };

  // ---- categories (rules)
  const setRule = (i: number, patch: Partial<Rule>) => updateRules(rules.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const moveRule = (i: number, dir: -1 | 1) => {
    const j = i + dir; if (j < 0 || j >= rules.length) return;
    const next = [...rules]; [next[i], next[j]] = [next[j], next[i]];
    updateRules(next.map((r, k) => ({ ...r, sort: (k + 1) * 10 })));
  };
  const removeRule = (i: number) => updateRules(rules.filter((_, j) => j !== i).map((r, k) => ({ ...r, sort: (k + 1) * 10 })));
  const addRule = () => {
    const taken = new Set(rules.map((r) => r.key));
    let n = rules.length + 1, key = `category${n}`;
    while (taken.has(key)) key = `category${++n}`;
    const used = new Set(rules.map((r) => r.bg.toUpperCase()));
    const bg = (SWATCHES.find((s) => !used.has(s.bg)) ?? SWATCHES[0]).bg;
    updateRules([...rules, { key, label: `CATEGORY ${n}`, sort: (rules.length + 1) * 10, matchers: [], bg, fg: autoFg(bg) }]);
  };

  // ---- unit types → category (the simple picker)
  const previewByUnit = useMemo(() => new Map(preview.map((p) => [p.unit, p])), [preview]);
  /** Category key a type is explicitly assigned to (unitType matcher), if any. */
  const assignedKey = (type: string) => rules.find((r) => r.matchers.some((m) => m.type === "unitType" && m.value.toUpperCase() === type))?.key;
  /** Category the type currently resolves to (explicit, or inherited from an advanced rule), from the server preview. */
  const effectiveKey = (t: UnitType) => {
    const explicit = assignedKey(t.type);
    if (explicit) return explicit;
    const p = t.examples.map((u) => previewByUnit.get(u)).find(Boolean);
    return p && p.category !== "other" && p.category !== "override" ? p.category : NONE;
  };
  const assignType = (type: string, key: string) => {
    const next = rules.map((r) => ({ ...r, matchers: r.matchers.filter((m) => !(m.type === "unitType" && m.value.toUpperCase() === type)) }));
    if (key !== NONE) {
      const i = next.findIndex((r) => r.key === key);
      if (i >= 0) next[i] = { ...next[i], matchers: [...next[i].matchers, { type: "unitType", value: type }] };
    } else {
      // "No color" must also beat any advanced rule that would otherwise claim the type:
      // represent it as an explicit assignment to a hidden gray category.
      let grayRule = next.find((r) => r.key === "nocolor");
      if (!grayRule) {
        grayRule = { key: "nocolor", label: "OTHER", sort: 999, matchers: [], bg: gray.bg, fg: gray.fg, bgDark: gray.bgDark, fgDark: gray.fgDark };
        next.push(grayRule);
      }
      grayRule.matchers = [...grayRule.matchers, { type: "unitType", value: type }];
    }
    updateRules(next.filter((r) => r.key !== "nocolor" || r.matchers.length > 0));
  };
  const visibleRules = rules.filter((r) => r.key !== "nocolor");

  // ---- overrides
  const filteredUnits = useMemo(() => {
    const q = unitSearch.trim().toUpperCase();
    return q ? units.filter((u) => u.includes(q)).slice(0, 30) : [];
  }, [unitSearch, units]);
  const addOverride = (unit: string) => {
    if (overrides.some((o) => o.unit === unit)) return;
    const current = previewByUnit.get(unit);
    updateOverrides([...overrides, { unit, bg: current?.bg ?? "#1C1C1E", fg: current?.fg ?? "#FFFFFF", category: visibleRules[0]?.key }]);
    setUnitSearch("");
  };
  const setOverride = (i: number, patch: Partial<Override>) => updateOverrides(overrides.map((o, j) => (j === i ? { ...o, ...patch } : o)));
  const removeOverride = (i: number) => updateOverrides(overrides.filter((_, j) => j !== i));

  const chipStyle = (p: { bg: string; fg: string; bgDark?: string; fgDark?: string }) => ({
    background: dark ? (p.bgDark || p.bg) : p.bg, color: dark ? (p.fgDark || p.fg) : p.fg,
    borderRadius: 7, padding: "3px 8px", fontSize: 12, fontWeight: 700, display: "inline-block", margin: "0 5px 5px 0", letterSpacing: 0.3,
  });
  const ruleChip = (r: Rule) => ({ bg: isHex(r.bg) ? r.bg : gray.bg, fg: isHex(r.fg) ? r.fg! : autoFg(isHex(r.bg) ? r.bg : gray.bg), bgDark: r.bgDark, fgDark: r.fgDark });

  if (loading) return (
    <Card><Card.Body><Spinner className="spinner-border-sm me-2" /> Loading unit colors…</Card.Body></Card>
  );

  const unassignedCount = unitTypes.filter((t) => effectiveKey(t) === NONE).length;

  return (
    <Fragment>
      <Card>
        <Card.Body className="py-2 px-3 border-bottom border-light d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div>
            <span className="form-check-label fs-16">Unit colors</span>
            <span className={`fw-badge ms-2 ${configured ? "fw-badge--ok" : "fw-badge--neutral"}`}>
              {configured ? (presetTag ? `${presetTag.toUpperCase()} preset` : "Custom") : "Not configured — all gray"}
            </span>
            {dirty && <span className="fw-badge fw-badge--warn ms-2">Unsaved changes</span>}
          </div>
          <div className="d-flex flex-wrap gap-2">
            {presets.map((p) => (
              <Button key={p.key} variant="outline-secondary" size="sm" disabled={saving} onClick={() => setConfirm("preset")}>
                Apply {p.name} preset
              </Button>
            ))}
            <Button variant="outline-danger" size="sm" disabled={saving || !configured} onClick={() => setConfirm("reset")}>Reset to gray</Button>
            <Button variant="primary" size="sm" disabled={saving || !dirty} onClick={save}>
              {saving ? <><Spinner className="spinner-border-sm me-1" /> Saving…</> : <><i className="ri-save-line" /> Save unit colors</>}
            </Button>
          </div>
        </Card.Body>
        <Card.Body>
          <p className="text-muted fs-13 mb-3">
            Super admins only. Two steps: <strong>1.</strong> make color categories (Engine, Truck, Chief…). <strong>2.</strong> for each unit
            type seen in {localityName || "this locality"} (the letters before the number: E, TL, BN…), pick its category. Units of a type
            with no category stay gray. Changes reach the apps after you save.
          </p>
          {error && <div className="alert alert-danger py-2 fs-13">{error}</div>}
          {warnings.length > 0 && (
            <div className="alert alert-warning py-2 fs-13 mb-3">
              <strong>Low contrast (below WCAG AA 4.5:1):</strong>
              <ul className="mb-0 mt-1">{warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
            </div>
          )}

          <Row>
            <Col xs={12}>
              <label className="form-check-label fs-15 mb-2">1. Color categories</label>
              <Table size="sm" className="fw-table align-middle">
                <tbody>
                  {visibleRules.length === 0 && (
                    <tr><td className="text-muted fs-13">No categories yet. Add one, or apply a preset.</td></tr>
                  )}
                  {visibleRules.map((r) => {
                    const i = rules.indexOf(r);
                    return (
                      <tr key={r.key}>
                        <td style={{ width: 54 }}>
                          <Button variant="link" size="sm" className="p-0 me-1" disabled={i === 0} onClick={() => moveRule(i, -1)}><i className="ri-arrow-up-s-line" /></Button>
                          <Button variant="link" size="sm" className="p-0" disabled={i === rules.length - 1} onClick={() => moveRule(i, 1)}><i className="ri-arrow-down-s-line" /></Button>
                        </td>
                        <td>
                          <Form.Control size="sm" value={r.label} placeholder="Name (shows in the legend)" onChange={(e) => setRule(i, { label: e.target.value.toUpperCase() })} />
                        </td>
                        <td style={{ width: 170 }}>
                          <div className="d-flex align-items-center gap-1">
                            <Form.Control type="color" size="sm" style={{ width: 34, padding: 2 }} value={rgb6(r.bg)}
                              onChange={(e) => setRule(i, { bg: e.target.value.toUpperCase(), fg: autoFg(e.target.value.toUpperCase()), legend: undefined, legendDark: undefined, bgDark: undefined, fgDark: undefined })} />
                            <Form.Select size="sm" value={SWATCHES.find((s) => s.bg === r.bg.toUpperCase())?.bg ?? ""} onChange={(e) => e.target.value && setRule(i, { bg: e.target.value, fg: autoFg(e.target.value), legend: undefined, legendDark: undefined, bgDark: undefined, fgDark: undefined })}>
                              <option value="">Custom…</option>
                              {SWATCHES.map((s) => <option key={s.bg} value={s.bg}>{s.name}</option>)}
                            </Form.Select>
                          </div>
                        </td>
                        <td style={{ width: 110 }}><span style={chipStyle(ruleChip(r))}>{r.label || "ABC"}</span></td>
                        <td style={{ width: 30 }}><Button variant="link" size="sm" className="p-0 text-danger" onClick={() => removeRule(i)}><i className="ri-delete-bin-line" /></Button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
              <Button variant="outline-primary" size="sm" className="mb-4" onClick={addRule}><i className="ri-add-line" /> Add category</Button>
            </Col>

            <Col xs={12}>
              <label className="form-check-label fs-15 mb-2">
                2. Unit types in {localityName || "this locality"}
                <span className="text-muted fs-12 ms-2">{unitTypes.length} types from {units.length} units (last 180 days){unassignedCount ? ` · ${unassignedCount} still gray` : ""}</span>
              </label>
              <div className="mb-4">
                <Table size="sm" className="fw-table align-middle">
                  <thead><tr><th style={{ width: 110 }}>Type</th><th style={{ width: 70 }}>Units</th><th>Examples</th><th style={{ width: 240 }}>Category</th></tr></thead>
                  <tbody>
                    {unitTypes.length === 0 && <tr><td colSpan={4} className="text-muted fs-13">No units seen for this locality yet.</td></tr>}
                    {unitTypes.map((t) => {
                      const key = effectiveKey(t);
                      const explicit = !!assignedKey(t.type);
                      const ex = previewByUnit.get(t.examples[0]);
                      return (
                        <tr key={t.type}>
                          <td><strong>{t.type}</strong></td>
                          <td className="text-muted">{t.count}</td>
                          <td>
                            {t.examples.map((u) => {
                              const p = previewByUnit.get(u);
                              return <span key={u} style={chipStyle(p ?? { bg: gray.bg, fg: gray.fg })}>{u}</span>;
                            })}
                          </td>
                          <td>
                            <Form.Select size="sm" value={key} onChange={(e) => assignType(t.type, e.target.value)}
                              style={{ borderLeft: `6px solid ${ex ? (dark ? ex.bgDark : ex.bg) : gray.bg}` }}>
                              <option value={NONE}>No color (gray)</option>
                              {visibleRules.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                            </Form.Select>
                            {!explicit && key !== NONE && <span className="text-muted fs-11">matched by an advanced rule</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </Col>
          </Row>

          <Row>
            <Col xs={12}>
              <label className="form-check-label fs-15 mb-2">3. Single-unit exceptions (optional)</label>
              <Form.Control size="sm" style={{ maxWidth: 480 }} placeholder="Search a unit name (e.g. RESCUE 1) to give it its own color…"
                value={unitSearch} onChange={(e) => setUnitSearch(e.target.value)} />
              {filteredUnits.length > 0 && (
                <div className="border rounded mt-1 p-1" style={{ maxHeight: 140, overflowY: "auto" }}>
                  {filteredUnits.map((u) => (
                    <Button key={u} variant="light" size="sm" className="m-1" onClick={() => addOverride(u)}>{u}</Button>
                  ))}
                </div>
              )}
              {unitSearch.trim() && !units.some((u) => u === unitSearch.trim().toUpperCase()) && (
                <Button variant="link" size="sm" className="p-0 mt-1" onClick={() => addOverride(unitSearch.trim().toUpperCase())}>
                  Add "{unitSearch.trim().toUpperCase()}" anyway
                </Button>
              )}
              <Table size="sm" className="fw-table align-middle mt-2 mb-4">
                <tbody>
                  {overrides.length === 0 && <tr><td className="text-muted fs-13">None.</td></tr>}
                  {overrides.map((o, i) => (
                    <tr key={o.unit}>
                      <td style={{ width: 110 }}><strong>{o.unit}</strong></td>
                      <td>
                        <Form.Select size="sm" value={o.category || ""} onChange={(e) => setOverride(i, { category: e.target.value || undefined })}>
                          <option value="">(counts as: none)</option>
                          {visibleRules.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                        </Form.Select>
                      </td>
                      <td style={{ width: 150 }}>
                        <div className="d-flex align-items-center gap-1">
                          <Form.Control type="color" size="sm" style={{ width: 34, padding: 2 }} value={rgb6(o.bg)} onChange={(e) => setOverride(i, { bg: e.target.value.toUpperCase(), fg: autoFg(e.target.value.toUpperCase()) })} />
                          <span style={chipStyle({ bg: isHex(o.bg) ? o.bg : gray.bg, fg: o.fg || autoFg(isHex(o.bg) ? o.bg : gray.bg) })}>{o.unit}</span>
                        </div>
                      </td>
                      <td style={{ width: 30 }}><Button variant="link" size="sm" className="p-0 text-danger" onClick={() => removeOverride(i)}><i className="ri-delete-bin-line" /></Button></td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Col>
            <Col xs={12}>
              <div className="d-flex align-items-center justify-content-between mb-2">
                <label className="form-check-label fs-15">4. Preview — as the apps render it</label>
                <Form.Check type="switch" id="unit-colors-dark" label="Dark mode" checked={dark} onChange={(e) => setDark(e.target.checked)} />
              </div>
              <div className="rounded p-3" style={{ background: dark ? "#181A21" : "#FFFFFF", border: "1px solid rgba(0,0,0,.09)" }}>
                <div className="mb-2 d-flex flex-wrap align-items-center" style={{ gap: 10 }}>
                  {legend.length === 0 && <span className="text-muted fs-12">No legend (nothing configured)</span>}
                  {legend.filter((l) => l.key !== "nocolor").map((l) => (
                    <span key={l.key} className="d-inline-flex align-items-center" style={{ fontSize: 9.5, fontWeight: 700, color: dark ? "#9AA0AC" : "#6C6F78" }}>
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: dark ? l.colorDark : l.color, display: "inline-block", marginRight: 4 }} />{l.label}
                    </span>
                  ))}
                </div>
                <div>
                  {preview.length === 0 && <span className="text-muted fs-12">No units seen for this locality yet.</span>}
                  {preview.map((p) => <span key={p.unit} style={chipStyle(p)} title={p.category}>{p.unit}</span>)}
                </div>
              </div>
            </Col>
          </Row>

          <div className="mt-3">
            <Button variant="link" size="sm" className="p-0" onClick={() => setShowAdvanced(!showAdvanced)}>
              <i className={`ri-arrow-${showAdvanced ? "down" : "right"}-s-line`} /> Advanced matching (prefixes and regular expressions)
            </Button>
            {showAdvanced && (
              <Table size="sm" responsive className="fw-table align-middle mt-2">
                <thead>
                  <tr><th style={{ width: 150 }}>Category</th><th style={{ width: 110 }}>Key</th><th>Name starts with (comma-separated)</th><th>Regex (one per line)</th><th style={{ width: 90 }}>Dark chip</th><th style={{ width: 90 }}>Dark text</th><th style={{ width: 90 }}>Legend dot</th></tr>
                </thead>
                <tbody>
                  {visibleRules.map((r) => {
                    const i = rules.indexOf(r);
                    return (
                      <tr key={"adv" + r.key}>
                        <td><span style={chipStyle(ruleChip(r))}>{r.label}</span></td>
                        <td><Form.Control size="sm" value={r.key} onChange={(e) => setRule(i, { key: e.target.value })} /></td>
                        <td><Form.Control size="sm" defaultValue={matchersToText(r.matchers, "prefix")} placeholder="E-, ENGINE"
                          onBlur={(e) => setRule(i, { matchers: [...r.matchers.filter((m) => m.type !== "prefix"), ...textToMatchers(e.target.value, "prefix")] })} /></td>
                        <td><Form.Control size="sm" as="textarea" rows={1} defaultValue={matchersToText(r.matchers, "regex")} placeholder="^E[0-9]"
                          onBlur={(e) => setRule(i, { matchers: [...r.matchers.filter((m) => m.type !== "regex"), ...textToMatchers(e.target.value, "regex")] })} /></td>
                        {(["bgDark", "fgDark", "legend"] as const).map((k) => (
                          <td key={k}>
                            <div className="d-flex align-items-center gap-1">
                              <Form.Control type="color" size="sm" style={{ width: 34, padding: 2 }} value={rgb6((r as any)[k] || r.bg)} onChange={(e) => setRule(i, { [k]: e.target.value.toUpperCase() } as any)} />
                              <Form.Control size="sm" style={{ width: 86, fontFamily: "monospace", fontSize: 11 }} value={(r as any)[k] || ""} placeholder="—" onChange={(e) => setRule(i, { [k]: e.target.value } as any)} isInvalid={!!(r as any)[k] && !isHex((r as any)[k])} />
                            </div>
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
          </div>
          <p className="text-muted fs-12 mt-2 mb-0">
            Gray is what unassigned units and unconfigured localities get. Saved changes reach the apps within 10 minutes and on the next incident open.
          </p>
        </Card.Body>
      </Card>

      <Modal show={confirm !== null} onHide={() => setConfirm(null)} centered>
        <Modal.Header closeButton><Modal.Title className="fs-16">{confirm === "preset" ? "Apply the FDNY preset?" : "Reset to gray?"}</Modal.Title></Modal.Header>
        <Modal.Body className="fs-14">
          {confirm === "preset"
            ? <>This replaces every category and exception for <strong>{localityName || "this locality"}</strong> with the FDNY mapping (engine, truck/rescue, marine, chief, SOC) and saves immediately.</>
            : <>This removes the color scheme for <strong>{localityName || "this locality"}</strong>. Every unit will render gray in the apps until a scheme is configured again. Saves immediately.</>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setConfirm(null)}>Cancel</Button>
          {confirm === "preset"
            ? <Button variant="primary" size="sm" disabled={saving} onClick={() => applyPreset(presets[0]?.key || "fdny")}>Apply preset</Button>
            : <Button variant="danger" size="sm" disabled={saving} onClick={resetToGray}>Reset to gray</Button>}
        </Modal.Footer>
      </Modal>
    </Fragment>
  );
}
